import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, C, Callout } from "./primitives.js";

export const meta = {
  id: "viral-videos",
  title: "Viral video production (storyboard → scenes → MP4)",
  category: "Workflows",
  lastUpdated: "2026-05-26",
  description: "Article → LLM storyboard → per-scene images/videos/voiceovers → Remotion composer → ready-to-publish 9:16 MP4 for Reels/Shorts/TikTok.",
  relatedGuides: ["produce-article", "asset-storage", "distribute-article"],
  readTimeMin: 8,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Three CLI commands take a finished article and produce a viral 9:16 video ready for TikTok / IG Reels / YouTube Shorts. The pipeline chains: Sonnet writes a multi-scene storyboard → image-gen creates a still per scene → video-gen animates each still → ElevenLabs narrates each scene → Remotion composes the final MP4 with captions, transitions, and music bed. Approximate cost: $1–1.50 per viral.
      </Lead>

      <Callout kind="tip">
        Run order: <C>keywords storyboard generate &lt;id&gt;</C> → review with <C>keywords storyboard show &lt;id&gt;</C> → <C>keywords viral generate &lt;id&gt;</C>. The middle step lets you eyeball scene-level decisions (caption phrasing, motion choices) before spending on assets.
      </Callout>

      <H2>What a storyboard looks like</H2>
      <P>
        The storyboard is a JSON document (5-10 scenes, persisted on the keyword row in SQLite). Each scene carries:
      </P>
      <Ul>
        <Li><strong>imagePrompt</strong> — English Flux 2 Pro prompt. Concrete subject + environment + style. Avoid generic people.</Li>
        <Li><strong>hebrewCaption</strong> — short Hebrew phrase (≤8 words) burned into the frame as overlay text. RTL-aware.</Li>
        <Li><strong>narration</strong> — Hebrew sentence (6-15 words). ElevenLabs renders this to an MP3 synced 1:1 with the scene.</Li>
        <Li><strong>durationSec</strong> — target on-screen time. Stretched at render time to fit narration audio length if needed.</Li>
        <Li><strong>imageMotion</strong> — <C>static</C> | <C>subtle-zoom</C> | <C>pan-left</C> | <C>pan-right</C> | <C>ken-burns</C>. Applied as a CSS transform animation when there's no scene video clip.</Li>
        <Li><strong>transition</strong> — <C>cut</C> | <C>fade</C> | <C>slide-left</C> | <C>slide-up</C>. Currently fade-in only; other transitions ride on the same primitive.</Li>
      </Ul>

      <H2>The pipeline, step by step</H2>
      <Ul>
        <Li>
          <strong>1. Storyboard</strong>: <C>keywords storyboard generate &lt;id&gt;</C>. Sonnet 4.6 reads the article + brief + cluster context, produces JSON. ~$0.05 + 15s. The output is validated against the Zod schema before persisting; if Claude returns malformed JSON, the command surfaces the validation issue.
        </Li>
        <Li>
          <strong>2. Scene images</strong> (8 calls): Flux 2 Pro generates a portrait still per scene. ~$0.06 total + 30s. Stored under <C>data/images/&lt;slug&gt;/scenes/scene-00.png</C> ... <C>scene-NN.png</C>.
        </Li>
        <Li>
          <strong>3. Scene videos</strong> (8 calls, optional): Wan 2.2 image-to-video animates each still into a 4-8s MP4. ~$0.80 total + 2-4 min. Disable with <C>--no-scene-videos</C> to use animated stills only (saves $0.80 + ~2 min).
        </Li>
        <Li>
          <strong>4. Per-scene voiceover</strong> (8 calls): ElevenLabs renders each <C>narration</C> line to MP3. ~$0.20 + 30s.
        </Li>
        <Li>
          <strong>5. Compose</strong>: Remotion bundles the React composition + renders to MP4. Headless Chromium handles the layout — 10-30s for a 60s viral on a modern laptop. Free locally.
        </Li>
        <Li>
          <strong>6. Upload</strong>: final MP4 lands in <C>keyword_assets</C> with <C>kind: viral_final</C>, uploaded to Sanity + Drive (under <C>viral/</C> subfolder). The TikTok / IG Reels / YouTube Shorts distribute routes pick it up automatically once we wire the asset lookup.
        </Li>
      </Ul>

      <H2>Cost breakdown (per viral)</H2>
      <Ul>
        <Li>Storyboard (Sonnet 4.6): ~$0.05</Li>
        <Li>Scene images (8 × Flux 2 Pro): ~$0.06</Li>
        <Li>Scene videos (8 × Wan 2.2): ~$0.80 — skip with <C>--no-scene-videos</C> to save</Li>
        <Li>Per-scene voiceover (8 × ~150 chars): ~$0.20</Li>
        <Li>Compose (local Remotion render): $0 — Remotion Lambda would add ~$0.20 for serverless render</Li>
        <Li><strong>Total: ~$1.10/viral</strong> (or ~$0.31/viral with <C>--no-scene-videos</C>)</Li>
      </Ul>

      <H2>Iteration loop</H2>
      <P>
        Don't burn a full $1 every time you tweak. The standard fast-iteration loop:
      </P>
      <Ul>
        <Li>Generate a storyboard. Read it via <C>keywords storyboard show &lt;id&gt;</C>.</Li>
        <Li>Tweak the article if the storyboard's hook is weak — the LLM picks scenes from your prose. Better prose → better hook.</Li>
        <Li>First render: <C>keywords viral generate &lt;id&gt; --no-scene-videos --no-voiceover</C> → silent video with animated stills. ~$0.10 + 30s. Lets you eyeball pacing + caption placement.</Li>
        <Li>Once happy: drop the flags for the full ~$1 render.</Li>
        <Li>If a single scene's image is bad: delete it from <C>data/images/&lt;slug&gt;/scenes/</C> and re-run — the orchestrator skips existing files by default.</Li>
      </Ul>

      <H2>Failure modes</H2>
      <Ul>
        <Li>
          <strong>"storyboard model returned no parseable JSON"</strong>: Sonnet wrapped the response in prose. Re-run — non-deterministic. If it happens twice, the article is unusually short / structured oddly; expand it first.
        </Li>
        <Li>
          <strong>"storyboard JSON failed schema validation"</strong>: Sonnet emitted a malformed scene (e.g. too-long Hebrew caption, invalid transition). Re-run. Schema is strict by design — bad scenes break the render later.
        </Li>
        <Li>
          <strong>Composer fails with "Chromium not found"</strong>: Remotion's `@remotion/renderer` needs Chromium. First-run installs it automatically into <C>node_modules/@remotion/compositor-*</C>. On constrained environments (CI without write access), set <C>REMOTION_CHROMIUM_EXECUTABLE_PATH</C> to a system Chromium.
        </Li>
        <Li>
          <strong>"no scenes have a usable image"</strong>: all image-gen calls failed (Replicate token issue, quota, network). Check the warnings, run <C>keywords images generate &lt;id&gt;</C> manually first to isolate.
        </Li>
        <Li>
          <strong>Narration too fast / too slow</strong>: ElevenLabs Multilingual v2 reads at ~15 chars/sec for Hebrew. If a scene's <C>narration</C> is much longer than its <C>durationSec * 15 chars</C>, the renderer stretches the scene to fit (so audio never cuts), but pacing gets sluggish. Tighten the Sonnet prompt's "matches durationSec at ~3 words/sec" constraint if this recurs.
        </Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
